import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';

import { Service } from 'app/models/services';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

type ImageUploadMode = 'direct' | 'imageproduit';

@Component({
  selector: 'app-modifier-service',
  templateUrl: './modifier-service.component.html',
  styleUrls: ['./modifier-service.component.scss']
})
export class ModifierServiceComponent implements OnInit {
  // Service data
  service: Service = new Service();
  agenciesList: any[] = [];
  idService: string | null = null;
  selectedAgencyId: string | null = null;

  // Main image only
  selectedFiles: FileList | null = null;
  isUploading: boolean = false;
  uploadedMainImage: string | null = null;

  // ===== IMAGE UPLOAD MODE =====
  imageUploadMode: ImageUploadMode = 'direct';
  previewOriginalUrl: string | null = null;
  previewProcessedUrl: string | null = null;
  isAwaitingValidation: boolean = false;

  // Form state
  isSubmitting: boolean = false;

  // Category management (French/English)
  categoryInput: string = '';

  // Category management (Arabic)
  categoryInputArab: string = '';

  // Category management (English)
  categoryInputAnglais: string = '';

  // Video URL
  videoUrl: string = '';

  // hasAtelier toggle
  hasAtelier: boolean = false;

  // Agency languages – mis à jour dynamiquement
  agencyAvailableLanguages: string[] = ['FR'];

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private agenciesService: AgenciesService,
    private servicesService: ServicesService,
    private awsService: AwscontrolService,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.idService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadAgencies(() => this.loadServiceDetails());

    this.servicesService.refreshNedeed.subscribe(() => {
      this.loadServiceDetails();
    });

    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => console.log('✅ AWS S3 Connection Healthy:', response),
      error: (err) => console.warn('⚠️ AWS S3 Connection Warning:', err)
    });
  }

  loadAgencies(callback?: () => void): void {
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        this.agenciesList = data;
        console.log('📋 Agences chargées :', this.agenciesList.length);
        if (this.service && this.service.agencyName) {
          this.findAgencyAndUpdateLanguages();
        }
        if (callback) callback();
      },
      error: (error) => {
        console.error('❌ Erreur chargement agences :', error);
      }
    });
  }

  loadServiceDetails(): void {
    if (!this.idService) return;

    this.servicesService.getService(this.idService).subscribe({
      next: (data: Service) => {
        this.service = data;
        this.uploadedMainImage = data.imageUrl || null;
        this.videoUrl = data.videoUrl || '';
        this.hasAtelier = data.hasAtelier || false;

        this.service.nomarab = data.nomarab || '';
        this.service.categoryListarab = data.categoryListarab || [];

        this.service.nomanglais = data.nomanglais || '';
        this.service.categoryListanglais = data.categoryListanglais || [];

        this.findAgencyAndUpdateLanguages();

        this.cdr.detectChanges();

        console.log('✅ Service chargé, agence sélectionnée :', this.service.agencyName);
      },
      error: (error) => {
        console.error('❌ Erreur chargement service :', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger les détails du service',
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  private findAgencyAndUpdateLanguages(): void {
    const userId = (this.service as any).userId || (this.service as any).user?.id;
    let foundAgency = null;

    if (userId && this.agenciesList.length > 0) {
      foundAgency = this.agenciesList.find(a => a.id === userId);
    }

    if (!foundAgency && this.service.agencyName) {
      const trimmedName = this.service.agencyName.trim();
      foundAgency = this.agenciesList.find(a =>
        a.name.trim().toLowerCase() === trimmedName.toLowerCase()
      );
    }

    if (foundAgency) {
      this.selectedAgencyId = foundAgency.id;
      this.service.agencyName = foundAgency.name;
      this.updateAgencyLanguages(foundAgency);
      console.log('✅ Agence trouvée :', foundAgency.name, 'Langues :', this.agencyAvailableLanguages);
    } else {
      console.warn('⚠️ Aucune agence trouvée pour le service.');
      this.agencyAvailableLanguages = ['FR'];
      this.selectedAgencyId = null;
    }
    this.cdr.detectChanges();
  }

  onAgencyChange(agencyName: string): void {
    const found = this.agenciesList.find(a => a.name === agencyName);
    if (found) {
      this.selectedAgencyId = found.id;
      this.updateAgencyLanguages(found);
      console.log('🔄 Agence modifiée manuellement :', found.name, 'Langues :', this.agencyAvailableLanguages);
    }
  }

  private updateAgencyLanguages(agency: any): void {
    this.agencyAvailableLanguages = agency.availableLanguages || ['FR'];
    if (this.agencyAvailableLanguages.length === 0) {
      this.agencyAvailableLanguages = ['FR'];
    }
    this.cdr.detectChanges();
  }

  // ----- Format text -----
  formatText(command: string): void {
    if (!this.service.description) this.service.description = '';
    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.service.description.substring(start, end);
    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      case 'linebreak':
        formattedText = '<br>';
        break;
      default: return;
    }
    const newText = this.service.description.substring(0, start) + formattedText + this.service.description.substring(end);
    this.service.description = newText;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  // ----- Categories (French/English) -----
  addCategory(): void {
    if (this.categoryInput.trim()) {
      if (!this.service.categoryList) {
        this.service.categoryList = [];
      }
      this.service.categoryList.push(this.categoryInput.trim());
      this.categoryInput = '';
    }
  }

  removeCategory(index: number): void {
    if (this.service.categoryList) {
      this.service.categoryList.splice(index, 1);
    }
  }

  // ----- Categories (Arabic) -----
  addCategoryArab(): void {
    if (this.categoryInputArab.trim()) {
      if (!this.service.categoryListarab) {
        this.service.categoryListarab = [];
      }
      this.service.categoryListarab.push(this.categoryInputArab.trim());
      this.categoryInputArab = '';
    }
  }

  removeCategoryArab(index: number): void {
    if (this.service.categoryListarab) {
      this.service.categoryListarab.splice(index, 1);
    }
  }

  // ----- Categories (English) -----
  addCategoryAnglais(): void {
    if (this.categoryInputAnglais.trim()) {
      if (!this.service.categoryListanglais) {
        this.service.categoryListanglais = [];
      }
      this.service.categoryListanglais.push(this.categoryInputAnglais.trim());
      this.categoryInputAnglais = '';
    }
  }

  removeCategoryAnglais(index: number): void {
    if (this.service.categoryListanglais) {
      this.service.categoryListanglais.splice(index, 1);
    }
  }

  // ==================== IMAGE UPLOAD ====================

  /**
   * Change le mode d'upload : 'direct' ou 'imageproduit'
   */
  setUploadMode(mode: ImageUploadMode): void {
    if (this.isUploading) return;
    if (this.isAwaitingValidation) {
      this.cancelImagePreview(true);
    }
    this.imageUploadMode = mode;
    console.log('🔄 Mode upload image :', mode);
    this.cdr.detectChanges();
  }

  /**
   * Ouvre la boîte de dialogue de sélection de fichier
   */
  triggerFileInput(): void {
    const input = document.getElementById('mainImageInput') as HTMLInputElement;
    if (input) {
      input.value = '';
      input.click();
    }
  }

  /**
   * Sélection d'un fichier → selon le mode, upload direct ou prévisualise
   */
  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!this.validateImageFile(file)) {
      target.value = '';
      return;
    }

    this.selectedFiles = files;

    // Aperçu local de l'original
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewOriginalUrl = (e.target?.result as string) || null;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);

    if (this.imageUploadMode === 'direct') {
      this.uploadMainImage();
    } else {
      this.uploadMainImageViaImageproduit();
    }
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: 'warning',
        title: 'Format non supporté',
        text: 'JPEG, PNG, JPG, GIF, WEBP uniquement',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier trop volumineux',
        text: 'Max 5MB',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    return true;
  }

  /**
   * Upload direct classique
   */
  uploadMainImage(): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    const file = this.selectedFiles[0];

    this.isUploading = true;
    const folder = 'service-images';

    this.awsService.uploadFile(file, folder).subscribe({
      next: (response: UploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          const url = response.urls[0];
          this.uploadedMainImage = url;
          this.service.imageUrl = url;
          this.previewOriginalUrl = null;
          Swal.fire({
            icon: 'success',
            title: 'Image mise à jour',
            text: 'L\'image principale a été téléchargée avec succès',
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: response.error || 'Échec du téléchargement',
            confirmButtonColor: '#f44336'
          });
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isUploading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de télécharger l\'image',
          confirmButtonColor: '#f44336'
        });
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Upload via imageproduit (maquette produit 555×428)
   * Après succès → panneau d'aperçu + validation
   */
  uploadMainImageViaImageproduit(): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    const file = this.selectedFiles[0];

    this.isUploading = true;
    this.isAwaitingValidation = false;
    this.previewProcessedUrl = null;

    const folder = 'imageproduit';

    this.awsService.imageproduit([file], folder).subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.previewProcessedUrl = response.urls[0];
          this.isAwaitingValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement de l\'image',
            confirmButtonColor: '#f44336'
          });
          this.previewOriginalUrl = null;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isUploading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de traiter l\'image',
          confirmButtonColor: '#f44336'
        });
        this.previewOriginalUrl = null;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * L'utilisateur valide l'image traitée → elle devient l'image du service
   */
  validateImagePreview(): void {
    if (!this.previewProcessedUrl) return;

    this.uploadedMainImage = this.previewProcessedUrl;
    this.service.imageUrl = this.previewProcessedUrl;

    this.previewOriginalUrl = null;
    this.previewProcessedUrl = null;
    this.isAwaitingValidation = false;
    this.selectedFiles = null;

    const input = document.getElementById('mainImageInput') as HTMLInputElement;
    if (input) input.value = '';

    this.cdr.detectChanges();

    Swal.fire({
      icon: 'success',
      title: 'Image validée',
      text: 'La nouvelle image sera utilisée pour ce service.',
      timer: 1500,
      showConfirmButton: false
    });
  }

  /**
   * L'utilisateur refuse l'image → suppression S3 + reset
   */
  cancelImagePreview(silent: boolean = false): void {
    const urlToDelete = this.previewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.previewOriginalUrl = null;
      this.previewProcessedUrl = null;
      this.isAwaitingValidation = false;
      this.selectedFiles = null;

      const input = document.getElementById('mainImageInput') as HTMLInputElement;
      if (input) input.value = '';

      this.cdr.detectChanges();
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: 'L\'image traitée sera supprimée et vous pourrez en choisir une autre.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  removeMainImage(): void {
    if (this.isAwaitingValidation) {
      this.cancelImagePreview();
      return;
    }

    Swal.fire({
      title: 'Supprimer l\'image?',
      text: 'Voulez-vous vraiment supprimer l\'image principale?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.uploadedMainImage = null;
        this.service.imageUrl = '';
        this.previewOriginalUrl = null;
        this.previewProcessedUrl = null;
        const input = document.getElementById('mainImageInput') as HTMLInputElement;
        if (input) input.value = '';
        this.cdr.detectChanges();
      }
    });
  }

  // ----- Submit -----
  updateService(): void {
    if (this.isAwaitingValidation) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation requise',
        text: 'Veuillez valider ou refuser l\'image avant de continuer.',
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    if (!this.service.name || this.service.name.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Nom requis',
        text: 'Veuillez saisir un nom pour le service',
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    this.isSubmitting = true;

    const serviceData = {
      name: this.service.name || '',
      nomarab: this.service.nomarab || '',
      nomanglais: this.service.nomanglais || '',
      description: this.service.description || '',
      agencyName: this.service.agencyName || '',
      categoryList: this.service.categoryList || [],
      categoryListarab: this.service.categoryListarab || [],
      categoryListanglais: this.service.categoryListanglais || [],
      imageUrl: this.service.imageUrl || '',
      videoUrl: this.videoUrl || '',
      hasAtelier: this.hasAtelier
    };

    console.log('Service data to update:', serviceData);

    if (!this.idService) {
      Swal.fire({
        icon: 'error',
        title: 'ID manquant',
        text: 'Impossible de mettre à jour le service sans ID',
        confirmButtonColor: '#f44336'
      });
      this.isSubmitting = false;
      return;
    }

    this.servicesService.updateService(this.idService, serviceData).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès!',
          text: 'Service mis à jour avec succès',
          confirmButtonColor: '#4caf50',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          this.goToServiceList();
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.handleError(error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = 'Une erreur est survenue lors de la mise à jour du service';
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.error?.text) {
      errorMessage = error.error.text;
    }

    Swal.fire({
      icon: 'error',
      title: 'Erreur',
      text: errorMessage,
      confirmButtonColor: '#f44336'
    });
  }

  // ----- Navigation -----
  private goToServiceList(): void {
    if (this.selectedAgencyId) {
      this.router.navigate(['/list-Services', this.selectedAgencyId]);
    } else {
      this.location.back();
    }
  }

  cancel(): void {
    this.goToServiceList();
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser les modifications?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadServiceDetails();
        this.categoryInput = '';
        this.categoryInputArab = '';
        this.categoryInputAnglais = '';

        // Reset preview state
        this.previewOriginalUrl = null;
        this.previewProcessedUrl = null;
        this.isAwaitingValidation = false;
        this.imageUploadMode = 'direct';

        const input = document.getElementById('mainImageInput') as HTMLInputElement;
        if (input) input.value = '';

        this.cdr.detectChanges();
      }
    });
  }
}